import type { User } from '@halo/chat-sdk-react';
import { useAppStore } from '@halo/chat-sdk-react';
import type { Dispatch, SetStateAction } from 'react';
import type { SearchGroupInfoType } from '@halo/chat-sdk-react';
import { MyTrans } from '@halo/ui-react';
import { i18n } from '@/store/i18n/i18n';
import { ResultContacts } from './ResultContacts';
import { ResultTopResults } from './ResultTopResults';
import { ResultGlobal } from './ResultGlobal';
import { ResultGroups } from './ResultGroups';
import { ResultMessages } from './ResultMessages';

export type GroupItemType = SearchGroupInfoType & { members_update_at: number };

export interface SearchResults {
  contactsUsers?: User[];
  globalUsers?: User[];
  globalData?: any[];
  groupsData?: GroupItemType[];
  globalGroupsData?: GroupItemType[];
  messagesData?: {
    cli_msg_id: string;
    chat_id: string;
    payload: string;
    created_at: number;
    updated_at: number;
  }[];
  topResultsData?: any[];
  notContactsUsers?: User[];
}

export const useSearch = (
  searchValue: string,
  active: string,
  setSearchResults: Dispatch<SetStateAction<Record<SearchTabs, SearchResults>>>,
  isOpen: boolean
) => {
  const value = useDeferredValue(searchValue);
  const { client } = useAppStore();
  const { run: onSearch } = useDebounceFn(
    async () => {
      if (value) {
        switch (active) {
          case SearchTabs.Contacts: {
            const res = await client?.Api.common.oneSearchByKey(value, [
              'user_in_contact',
              'user_not_in_contact',
            ]);
            const { user_in_contact = [], user_not_in_contact = [] } = res ?? {};
            setSearchResults((prev) => ({
              ...prev,
              [SearchTabs.Contacts]: {
                contactsUsers: user_in_contact ?? [],
                notContactsUsers: user_not_in_contact,
              },
            }));
            break;
          }
          case SearchTabs.Global: {
            const res = await client?.Api.common.oneSearchByKey(value, [
              'user_and_group_in_global',
            ]);
            const { user_and_group_in_global } = res ?? {};
            // console.log('rrr', user_and_group_in_global)
            setSearchResults((prev) => ({
              ...prev,
              [SearchTabs.Global]: {
                globalData: user_and_group_in_global ?? [],
              },
            }));
            break;
          }
          case SearchTabs.TopResults: {
            const res = await client?.Api.common.oneSearchByKey(value, [
              'user_in_contact',
              'user_not_in_contact',
              'group_in_contact',
              'group_not_in_contact',
              'message',
              'history',
            ]);

            const {
              user_in_contact = [],
              user_not_in_contact = [],
              group_in_contact = [],
              group_not_in_contact = [],
              message = [],
            } = res ?? {};

            setSearchResults((prev) => ({
              ...prev,
              [SearchTabs.TopResults]: {
                contactsUsers: user_in_contact ?? [],
                notContactsUsers: user_not_in_contact,
                groupsData: group_in_contact,
                globalGroupsData: group_not_in_contact,
                messagesData: message,
              },
            }));
            break;
          }
          case SearchTabs.Messages: {
            const res = await client?.Api.common.oneSearchByKey(value, ['message']);
            const { message } = res ?? {};
            setSearchResults((prev) => ({
              ...prev,
              [SearchTabs.Messages]: {
                messagesData: message ?? [],
              },
            }));
            break;
          }
          case SearchTabs.Groups: {
            const res = await client?.Api.common.oneSearchByKey(value, [
              'group_in_contact',
              'group_not_in_contact',
            ]);
            const { group_in_contact = [], group_not_in_contact = [] } = res ?? {};
            setSearchResults((prev) => ({
              ...prev,
              [SearchTabs.Groups]: {
                groupsData: group_in_contact,
                globalGroupsData: group_not_in_contact,
              },
            }));
            break;
          }
          default:
            break;
        }
      }
    },
    { wait: 1000 }
  );

  useEffect(() => {
    if (isOpen) {
      onSearch();
    }
  }, [value, onSearch, isOpen]);

  return { onSearch };
};

export enum SearchTabs {
  TopResults = 'TopResults',
  Global = 'Global',
  Contacts = 'Contacts',
  Groups = 'Groups',
  Messages = 'Messages',
}

export const defaultSearchResults: Record<SearchTabs, SearchResults> = {
  [SearchTabs.Contacts]: { contactsUsers: [], notContactsUsers: [] },
  [SearchTabs.Global]: { globalData: [] },
  [SearchTabs.Groups]: { groupsData: [] },
  [SearchTabs.Messages]: { messagesData: [] },
  [SearchTabs.TopResults]: { topResultsData: [], contactsUsers: [], notContactsUsers: [] },
};

export const getTabs = (
  searchValue: string,
  searchResults: Record<SearchTabs, SearchResults>,
  setActive: Dispatch<SetStateAction<SearchTabs>>,
  setValue: Dispatch<SetStateAction<string>>,
  active: SearchTabs
) => [
  {
    value: SearchTabs.TopResults,
    label: <MyTrans i18nKey="filter.searchByTopResults" />,
    content: (
      <ResultTopResults
        searchValue={searchValue}
        searchResults={searchResults[SearchTabs.TopResults]}
        setActive={setActive}
        onSearch={setValue}
        active={active}
      />
    ),
  },
  {
    value: SearchTabs.Global,
    label: <MyTrans i18nKey="filter.searchByGlobal" />,
    content: (
      <ResultGlobal searchResults={searchResults[SearchTabs.Global]} searchValue={searchValue} />
    ),
  },
  {
    value: SearchTabs.Contacts,
    label: <MyTrans i18nKey="filter.searchByContacts" />,
    content: (
      <ResultContacts
        searchResults={searchResults[SearchTabs.Contacts]}
        searchValue={searchValue}
        setActive={setActive}
      />
    ),
    searchPlaceholder: i18n.t('filter.searchContactsPlaceholder'),
  },
  {
    value: SearchTabs.Groups,
    label: <MyTrans i18nKey="filter.searchByGroups" />,
    content: (
      <ResultGroups
        setActive={setActive}
        searchValue={searchValue}
        searchResults={searchResults[SearchTabs.Groups]}
      />
    ),
  },
  {
    value: SearchTabs.Messages,
    label: <MyTrans i18nKey="filter.searchByMessages" />,
    content: (
      <ResultMessages
        searchValue={searchValue}
        searchResults={searchResults[SearchTabs.Messages]}
      />
    ),
    searchPlaceholder: i18n.t('filter.searchMessagesPlaceholder'),
  },
];
